import { PAGINATION_DEFAULT_LIMIT } from 'app.constants'
import { Customer } from 'types'
import { usePaginationHandler, getShopId, useQueryHandler, useMutationHandler } from 'utils'

const customerQuery = `
    id
    firstName
    lastName
    email
    phone
    image{
        id
        path
    }
    description
    externalId
    externalSource
`

export const useCustomers = (
    search: string | null,
    limit: number = PAGINATION_DEFAULT_LIMIT,
    enabled?: boolean
) => {
    const shopId = getShopId()
    return usePaginationHandler<Customer[]>({
        query: `
            query getCustomers(
                $shop: String!,
                $searchTerm: String,
                $limit: Int!,
                $after: String,
                $before: String
            ){
                getCustomers(
                    shop: $shop,
                    searchTerm: $searchTerm,
                    limit: $limit,
                    after: $after,
                    before: $before
                ){
                    data{
                        ${customerQuery}
                    }
                    totalItems
                    remainingItems
                    nextCursor
                    previousCursor
                }
            }
        `,
        variables: {
            shop: shopId,
            searchTerm: search,
            limit,
            after: null,
            before: null
        },
        cacheKey: ['customers', shopId, search],
        limit,
        enabled,
        getResponse: json => json.data.getCustomers,
        onSuccess: response => response.data
    })
}

export const useCustomer = (
    id: string,
    enabled?: boolean,
    onNotFound?: (json: any) => void
) => {
    const shopId = getShopId()
    return useQueryHandler<Customer>({
        query: `
            query getCustomer(
                $shop: String!,
                $id: String!
            ){
                getCustomer(
                    shop: $shop,
                    id: $id
                ){
                    ${customerQuery}
                }
            }
        `,
        variables: {
            shop: shopId,
            id
        },
        cacheKey: ['customer', id, shopId],
        enabled,
        onSuccess: json => json.data.getCustomer,
        onNotFound
    })
}

export const useCreateCustomer = (customer: Customer) => (
    useMutationHandler<Customer | 'customer_exists' | null>({
        query: `
            mutation createCustomer(
                $shop: String!,
                $request: CreateCustomerRequest!,
            ){
                createCustomer(
                    shop: $shop,
                    request: $request
                ){
                    ${customerQuery}
                }
            }
        `,
        variables: {
            shop: getShopId(),
            request: {
                firstName: customer.firstName,
                lastName: customer.lastName,
                email: customer.email,
                phone: customer.phone,
                image: customer.image.id,
                description: customer.description,
                externalId: customer.externalId,
                externalSource: customer.externalSource
            }
        },
        onSuccess: json => json.data.createCustomer,
        onError: json => {
            if(json.errors[0].extensions.errors[0].constraints[0].name === 'is_unique'){
                return 'customer_exists'
            }else{
                return null
            }
        }
    })
)

export const useUpdateCustomer = (customer: Customer) => (
    useMutationHandler<boolean | 'customer_exists' | null>({
        query: `
            mutation updateCustomer(
                $shop: String!,
                $id: String!,
                $request: UpdateCustomerRequest!
            ){
                updateCustomer(
                    shop: $shop,
                    id: $id,
                    request: $request
                ){
                    id
                }
            }
        `,
        variables: {
            shop: getShopId(),
            id: customer.id,
            request: {
                firstName: customer.firstName,
                lastName: customer.lastName,
                email: customer.email,
                phone: customer.phone,
                image: customer.image.id,
                description: customer.description
            }
        },
        onSuccess: json => json.data.updateCustomer.id === customer.id,
        onError: json => {
            if(json.errors[0].extensions.errors[0].constraints[0].name === 'is_unique'){
                return 'customer_exists'
            }else{
                return null
            }
        }
    })
)

export const useRemoveCustomer = (id: string) => (
    useMutationHandler<boolean>({
        query: `
            mutation deleteCustomer(
                $shop: String!,
                $id: String!
            ){
                deleteCustomer(
                    shop: $shop,
                    id: $id
                ){
                    message
                }
            }
        `,
        variables: {
            shop: getShopId(),
            id
        },
        onSuccess: json => json.data.deleteCustomer.message === 'Done'
    })
)
